import { t } from "@lingui/core/macro";
import dayjs, { type Dayjs } from "dayjs";
import { create } from "zustand";
import { useAppointmentModalStore } from "../../../../Components/AppointmentModal/useAppointmentModalStore_";
import {
  apiGetCalendarData,
  apiUpdateCalendarFilter,
} from "../api/apiCalendar";
import "dayjs/locale/sl";
import { addToast } from "@heroui/react";
import type { GetTimeOff, TimeOffGranularity } from "@/server-types";

/**
 * Deliberately untyped. These are forwarded straight through the calendar to
 * consumers this refactor does not cover; pinning them here would mean
 * re-typing all 39 of them in one commit. The time-off path below is the part
 * this file makes type-safe.
 */
// biome-ignore lint/suspicious/noExplicitAny: incremental typing, see above
type Untyped = any;

export type CalendarView = "day" | "week" | "month";

export type CalendarMode = "users" | "resources";

/**
 * `GET /calendar/timeoff` verbatim, so a change to that response is a type
 * error in the columns that render it rather than a blank block on the
 * calendar.
 */
export type CalendarTimeOffs = GetTimeOff["response"];

export type CalendarTimeOff = CalendarTimeOffs["timeoffs"][number];

export type CalendarShift = {
  userId: number;
  start: string;
  end: string;
};

export type CalendarDayUser = { userId: number } & Untyped;

export type CalendarDay = {
  date: string;
  users: Untyped[];
  disableOnHolidays?: boolean;
};

export type CalendarAppointment = { appointmentId: number } & Untyped;

/**
 * The `/calendar/2` payload. Every field is optional because the store holds an
 * empty object until the first fetch lands, and the columns already guard on
 * that.
 */
export type CalendarData = {
  days: CalendarDay[];
  view: CalendarView;
  startTime: string;
  endTime: string;
  timeStep: number;
  slotHeight: number;
  calendarHeight: number;
  allShifts: CalendarShift[];
  allAppointments: Untyped;
  currency: string;
  defaultPaymentTimeoutMinutes: number;
  defaultAllowDuplicateCustomerOnAppointment: boolean;
  isPaymentDefaultCheckedInDashboard: boolean;
};

export type CalendarLocation = { locationId: number } & Untyped;

export type CalendarResource = { id: number } & Untyped;

export type CalendarFilterItems = {
  showOnlyUsersWithShifts: boolean;
  showStaffNames: boolean;
} & Untyped;

/** The occurrence a click on the calendar opened for editing. */
export type SelectedTimeOff = {
  id: number;
  source: "staff" | "resource";
  seriesId: number | null;
};

/**
 * A time off dropped somewhere new, held until a scope is chosen. Only a series
 * occurrence waits here; a standalone one is written on drop.
 */
export type PendingTimeOffDrop = {
  id: number;
  source: "staff" | "resource";
  date: string;
  startTime: string;
  userId: number | null;
  resourceId: number | null;
  /** False once the drop moves the row off its day or onto another target. */
  allowFollowing: boolean;
};

export type NewBreakData = {
  userId: number;
  date: string;
  startTime: string;
  /** A staff header click opens the form on a day-level type. */
  granularity?: TimeOffGranularity;
  /** Opens the drawer on the Odsotnost tab rather than on Termin. */
  opensTimeOffTab?: boolean;
} & Untyped;

export type RedLineData = {
  currentTime: string;
  yOffset: number;
};

/**
 * Lists the store's own surface. Collections the calendar only forwards —
 * services, tags, appointments — stay deliberately loose: this file types the
 * time-off render path, and pinning the rest would mean re-typing all 39
 * consumers at once.
 */
type CalendarStore = {
  selectedDate: Dayjs;
  calendarView: Untyped;
  fetchingCalendarData: boolean;
  isRefetchingCalendarDate: boolean;
  locations: CalendarLocation[];
  users: CalendarDayUser[];
  locationStaffCount: number | null;
  services: Untyped[];
  serviceTags: Untyped[];
  timeoffs: CalendarTimeOffs | undefined;
  resources: CalendarResource[];
  calendarMode: CalendarMode;
  hasRescheduleNotification: boolean;
  hasCancelNotification: boolean;
  hasNoShowNotification: boolean;
  selectedLocationId: Untyped;
  filterItems: CalendarFilterItems;
  showOnlyFixedAppointments: boolean;
  setShowOnlyFixedAppointments: (value: boolean) => void;
  lastSelectedUserId: Untyped;
  lastSelectedTime: Untyped;
  lastSelectedDate: Untyped;
  lastSelectedResourceId: Untyped;
  doesHaveCombinations: boolean;
  setDoesHaveCombinations: (value: boolean) => void;
  hideModalTabs: boolean;
  setHideModalTabs: (value: boolean) => void;
  isExpandedModal: boolean;
  setIsExpandedModal: (value: boolean) => void;
  draggingId: number | null;
  setDraggingId: (value: number | null) => void;
  lastClickYOffset: number;
  setLastClickYOffset: (value: number) => void;
  closeModals: () => void;
  toggleCalendarMode: () => void;
  setTimeoffs: (timeoffs: CalendarTimeOffs | undefined) => void;
  newBreakData: NewBreakData | null;
  setNewBreakData: (value: NewBreakData | null) => void;
  selectedTimeOff: SelectedTimeOff | null;
  setSelectedTimeOff: (value: SelectedTimeOff | null) => void;
  pendingTimeOffDrop: PendingTimeOffDrop | null;
  setPendingTimeOffDrop: (value: PendingTimeOffDrop | null) => void;
  tagEnforcedOrders: Untyped[];
  newPreScheduledAppointmentData: Untyped;
  setNewPreScheduledAppointmentData: (value: Untyped) => void;
  setLastSelectedTime: (time: Untyped) => void;
  setLastSelectedDate: (date: Untyped) => void;
  setLastSelectedUserId: (userId: Untyped) => void;
  setLastSelectedResourceId: (resourceId: Untyped) => void;
  rescheduleData: Untyped;
  setRescheduleData: (value: Untyped) => void;
  calendarData: CalendarData;
  setCalendarData: (calendarData: CalendarData) => void;
  updateAppointmentInCalendar: (appointment: CalendarAppointment) => void;
  viewMode: string;
  refreshWithNewFilter: (filterItems: CalendarFilterItems) => Promise<void>;
  refreshCalendar: (isRefetching?: boolean) => Promise<void>;
  selectedAppointmentId: Untyped;
  selectedBreakId: Untyped;
  setSelectedAppointmentId: (appointmentId: Untyped) => void;
  dateActions: {
    setSelectedDateAndView: (date: Dayjs, view: Untyped) => void;
    setSelectedDate: (date: Dayjs, forceRefresh?: boolean) => void;
    setCalendarView: (view: Untyped) => void;
  };
  locationActions: {
    setLocations: (locations: CalendarLocation[]) => void;
    setSelectedLocationId: (id: Untyped) => void;
  };
  userActions: { setUsers: (users: Untyped[]) => void };
  resourceActions: { setResources: (resources: CalendarResource[]) => void };
  serviceActions: { setServices: (services: Untyped[]) => void };
  filterActions: { setFilterItems: (filter: CalendarFilterItems) => void };
};

export const useCalendarStore = create<CalendarStore>()((set, get) => ({
  selectedDate: dayjs().locale("sl"),
  calendarView: null,

  fetchingCalendarData: false,
  isRefetchingCalendarDate: false,

  locations: [],
  users: [],
  // How many staff the selected location has, regardless of which of them this
  // viewer may see. `null` until the calendar payload lands.
  locationStaffCount: null,
  services: [],
  serviceTags: [],
  timeoffs: undefined,
  resources: [],
  calendarMode: "resources",

  hasRescheduleNotification: false,
  hasCancelNotification: false,
  hasNoShowNotification: false,

  selectedLocationId: null,

  filterItems: {
    location: null,
    users: [],
    services: [],
    days: [],
    resources: [],
    showOnlyUsersWithShifts: false,
    showStaffNames: false,
  },

  showOnlyFixedAppointments: false, // client side only
  setShowOnlyFixedAppointments: (showOnlyFixedAppointments) =>
    set({ showOnlyFixedAppointments }),

  lastSelectedUserId: null,
  lastSelectedTime: null,
  lastSelectedDate: null,
  lastSelectedResourceId: null,

  doesHaveCombinations: false,
  setDoesHaveCombinations: (doesHaveCombinations) =>
    set({ doesHaveCombinations }),

  hideModalTabs: false,
  setHideModalTabs: (hideModalTabs) => set({ hideModalTabs }),

  isExpandedModal: false,
  setIsExpandedModal: (isExpandedModal) => set({ isExpandedModal }),

  draggingId: null,
  setDraggingId: (draggingId) => set({ draggingId }),

  lastClickYOffset: 0,
  setLastClickYOffset: (lastClickYOffset) => set({ lastClickYOffset }),

  closeModals: () => {
    set({
      selectedAppointmentId: null,
      selectedBreakId: null,
      newBreakData: null,
      selectedTimeOff: null,
      rescheduleData: null,
      hideModalTabs: false,
      isExpandedModal: false,
    });
    useAppointmentModalStore.getState().resetData();
    useAppointmentModalStore.getState().setCurrentAppointmentGsm(null);
    useAppointmentModalStore.getState().setCurrentAppointmentEmail(null);
    useAppointmentModalStore.setState({
      isCreatingAppointment: false,
    });

    get().refreshCalendar();
  },

  toggleCalendarMode: () => {
    if (get().calendarMode === "users") {
      set({ calendarMode: "resources" });
    } else {
      set({ calendarMode: "users" });
    }
  },

  setTimeoffs: (timeoffs) => set({ timeoffs }),

  /* Quick shift break creation */
  newBreakData: null,
  setNewBreakData: (newBreakData) => set({ newBreakData }),

  selectedTimeOff: null,
  setSelectedTimeOff: (selectedTimeOff) => set({ selectedTimeOff }),

  pendingTimeOffDrop: null,
  setPendingTimeOffDrop: (pendingTimeOffDrop) => set({ pendingTimeOffDrop }),

  tagEnforcedOrders: [],

  /* PreScheduled appointment creation */
  newPreScheduledAppointmentData: null,
  setNewPreScheduledAppointmentData: (newPreScheduledAppointmentData) =>
    set({ newPreScheduledAppointmentData }),

  setLastSelectedTime: (time) => set({ lastSelectedTime: time }),
  setLastSelectedDate: (date) => set({ lastSelectedDate: date }),
  setLastSelectedUserId: (userId) => set({ lastSelectedUserId: userId }),
  setLastSelectedResourceId: (resourceId) =>
    set({ lastSelectedResourceId: resourceId }),

  rescheduleData: null,
  setRescheduleData: (rescheduleData) => set({ rescheduleData }),

  calendarData: {} as CalendarData,
  setCalendarData: (calendarData) => set({ calendarData }),

  updateAppointmentInCalendar: (updatedAppointment) => {
    set((state) => {
      const newCalendarData = { ...state.calendarData };
      const appointments = [...newCalendarData.allAppointments];

      const appointmentIndex = appointments.findIndex(
        (app) => app.appointmentId === updatedAppointment.appointmentId,
      );

      if (appointmentIndex !== -1) {
        const originalAppointment = appointments[appointmentIndex];

        appointments[appointmentIndex] = {
          ...originalAppointment,
          ...updatedAppointment,
          users: updatedAppointment.users || originalAppointment.users,
          userIds: updatedAppointment.userIds || originalAppointment.userIds,
          resources:
            updatedAppointment.resources || originalAppointment.resources,
          filterEnabled: originalAppointment.filterEnabled,
          overlapCount: originalAppointment.overlapCount,
          overlapIndex: originalAppointment.overlapIndex,
        };

        newCalendarData.allAppointments = appointments;
      }

      return { calendarData: newCalendarData };
    });
  },

  viewMode: "appointments",

  refreshWithNewFilter: async (filterItems) => {
    // Save against the location the draft belongs to, not the live store
    // location — otherwise a location switch can persist one location's
    // selection against another, disabling staff (DEV-8678).
    const locationId = filterItems.FK_locationId ?? get().selectedLocationId;
    await apiUpdateCalendarFilter(locationId, filterItems).catch((e) =>
      addToast({
        title: t`Nimate dovoljenja za urejanje filtra`,
        color: "danger",
      }),
    );
    await get().refreshCalendar();
  },

  refreshCalendar: async (isRefetching = false) => {
    if (get().fetchingCalendarData) return;

    set({ fetchingCalendarData: true, isRefetchingCalendarDate: isRefetching });

    const calendarDataResponse = await apiGetCalendarData({
      locationId: get().selectedLocationId,
      date: get().selectedDate.format("YYYY-MM-DD"),
      view: get().calendarView,
    }).catch((err) => {});

    set({ fetchingCalendarData: false, isRefetchingCalendarDate: false });

    if (calendarDataResponse == null || calendarDataResponse?.status !== 200)
      return;

    const locations = calendarDataResponse.data.locations;
    get().locationActions.setLocations(locations);
    if (locations.length === 0) {
      console.error("No locations found.");
      return;
    }

    if (get().selectedLocationId == null) {
      get().locationActions.setSelectedLocationId(
        calendarDataResponse.data.filter?.FK_locationId ??
          locations[0].locationId,
      );
    }

    if (get().calendarView == null) {
      get().dateActions.setCalendarView(
        calendarDataResponse.data.calendarData.view,
      );
    }

    const users = calendarDataResponse.data.users;
    get().userActions.setUsers(users);

    const services = calendarDataResponse.data.services;
    get().serviceActions.setServices(services);

    const resources = calendarDataResponse.data.resources;
    get().resourceActions.setResources(resources);

    get().filterActions.setFilterItems(calendarDataResponse.data.filter);

    const calendarData = calendarDataResponse.data.calendarData;
    set({
      calendarData,
      serviceTags: calendarDataResponse.data.allTags,
      fetchingCalendarData: false,
      hasRescheduleNotification:
        calendarDataResponse.data.hasRescheduleNotification,
      hasCancelNotification: calendarDataResponse.data.hasCancelNotification,
      hasNoShowNotification: calendarDataResponse.data.hasNoShowNotification,
      calendarMode: calendarDataResponse.data.filter.calendarMode,
      locationStaffCount: calendarDataResponse.data.locationStaffCount ?? null,
      doesHaveCombinations: calendarDataResponse.data.doesHaveCombinations,
      tagEnforcedOrders: calendarDataResponse.data.tagEnforcedOrders,
    });
  },

  selectedAppointmentId: null, // -1 for new appointment
  selectedBreakId: null,
  setSelectedAppointmentId: (appointmentId) => {
    if (appointmentId == null) {
      get().refreshCalendar();
    }

    set({ selectedAppointmentId: appointmentId });
  },

  dateActions: {
    setSelectedDateAndView: (date, view) => {
      set({ selectedDate: date, calendarView: view });
      get().refreshCalendar();
    },

    setSelectedDate: (date, forceRefresh = false) => {
      const isSameDate = get().selectedDate.isSame(date, "day");

      set({ selectedDate: date });

      if (!isSameDate || forceRefresh) {
        get().refreshCalendar();
      }
    },
    setCalendarView: (view) => {
      const isSameView = get().calendarView === view;

      set({ calendarView: view });

      // only refresh calendar if not settings to same view
      if (!isSameView) {
        get().refreshCalendar();
      }
    },
  },
  locationActions: {
    setLocations: (locations) => set({ locations }),
    setSelectedLocationId: (id) => {
      set({ selectedLocationId: id });
    },
  },
  userActions: {
    setUsers: (users) => set({ users }),
  },
  resourceActions: {
    setResources: (resources) => set({ resources }),
  },
  serviceActions: {
    setServices: (services) => set({ services }),
  },
  filterActions: {
    setFilterItems: (filter) => {
      set({ filterItems: filter });
    },
  },
}));
